import React, { useState, useEffect, useRef, useMemo } from 'react';
import api from '../api/api';
import RecipeTree from './utils/RecipeTree';
import ExplanationWithTimes from './utils/ExplanationWithTimes';
import { Link, useNavigate } from 'react-router-dom';
import ServingsAdjuster from './ServingsAdjuster';
import { Clock, ChefHat, CheckCircle, Circle, X, Play, Trash, Maximize2 } from 'lucide-react';

var CDN = process.env.NODE_ENV==='development' ? 'https://cdn.j7lab.com/dev' : 'https://cdn.j7lab.com';

const sanitizeImageBase = (value) => {
  if (!value) return null;
  return value.endsWith('/') ? value.slice(0, -1) : value;
};

const INGREDIENT_IMAGE_BASE = sanitizeImageBase(CDN);

const normalizeIngredientKey = (value) => {
  if (!value) return null;
  return value
    .toString()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
};

const extractStandardIngredientId = (ingredient) => {
  if (!ingredient) return null;
  const direct = ingredient.standardIngredient ?? ingredient.standardIngredientId;
  if (direct && typeof direct === 'string') return direct;
  if (direct && typeof direct === 'object' && direct._id) return direct._id;
  return null;
};

const normalizeId = (value) => {
  if (value == null) return null;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed || null;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  if (typeof value === 'object') {
    if (value._id != null) return normalizeId(value._id);
    if (value.id != null) return normalizeId(value.id);
  }
  return null;
};

const slugify = (value) => {
  if (!value) return '';
  return value
    .toString()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/-{2,}/g, '-');
};

const buildIngredientDetailUrl = (ingredient) => {
  if (!ingredient) return null;

  const standardSource = typeof ingredient.standardIngredient === 'object'
    ? ingredient.standardIngredient
    : null;

  const candidateIds = [
    ingredient.standardIngredientId,
    ingredient.standardIngredient,
    ingredient.normalizedIngredientId,
    standardSource,
  ];

  const id = candidateIds.map(normalizeId).find(Boolean);
  if (!id) return null;

  const slugSource =
    (typeof ingredient.standardIngredientSlug === 'string' && ingredient.standardIngredientSlug.trim()) ||
    (typeof ingredient.slug === 'string' && ingredient.slug.trim()) ||
    (standardSource && typeof standardSource.slug === 'string' && standardSource.slug.trim()) ||
    (standardSource && typeof standardSource.name === 'string' && standardSource.name.trim()) ||
    (standardSource && typeof standardSource.label === 'string' && standardSource.label.trim()) ||
    (typeof ingredient.normalizedName === 'string' && ingredient.normalizedName.trim()) ||
    (typeof ingredient.name === 'string' && ingredient.name.trim()) ||
    id;

  const slug = slugify(slugSource);
  if (!slug) return null;

  return `/idees-de-repas/idees-recettes/${encodeURIComponent(slug)}/${encodeURIComponent(id)}`;
};

const buildIngredientImageUrl = (id, base) => {
  if (!id || !base) return null;
  return `${base}/ingredients/${id}.png`;
};

export const DishTypeBadge = ({ type }) => {
  const colors = {
    'Entrée': { bg: '#87A96B', text: 'white' },
    'Plat': { bg: '#E67E22', text: 'white' },
    'Dessert': { bg: '#87A96B', text: 'white' },
    'Goûter': { bg: '#E67E22', text: 'white' },
    'Apéritif': { bg: '#87A96B', text: 'white' },
    'Petit-déjeuner': { bg: '#E67E22', text: 'white' },
    'default': { bg: '#6c757d', text: 'white' }
  };

  const colorScheme = colors[type] || colors.default;

  return (
    <span 
      className="px-3 py-1.5 rounded-full text-sm font-medium shadow-sm"
      style={{ backgroundColor: colorScheme.bg, color: colorScheme.text }}
    >
      {type}
    </span>
  );
};

export const NutriScoreDisplay = ({
  nutriScore,
  nutriValues,
  totalWeight = 400,
  servings = 2,
  originalServings = 2
}) => {
  const [viewMode, setViewMode] = useState('per100g');
  const [showNutriValues, setShowNutriValues] = useState(false);
  const [showGlycemicImpact, setShowGlycemicImpact] = useState(false);

  const getScoreColor = (score) => {
    const colors = {
      1: '#87A96B',
      2: '#87A96B',
      3: '#E67E22',
      4: '#E67E22',
      5: '#dc2626'
    };
    return colors[score] || '#6c757d';
  };

  const getScoreLetter = (score) => {
    const letters = { 1: 'A', 2: 'B', 3: 'C', 4: 'D', 5: 'E' };
    return letters[score] || '?';
  };

  const safeServings = Number.isFinite(servings) && servings > 0 ? servings : 2;
  const safeOriginalServings = Number.isFinite(originalServings) && originalServings > 0
    ? originalServings
    : safeServings;
  const effectiveRatio = safeOriginalServings ? safeServings / safeOriginalServings : 1;
  const adjustedTotalWeight = Number.isFinite(totalWeight)
    ? totalWeight * effectiveRatio
    : undefined;

  const calculateValues = (baseValues, mode) => {
    if (!baseValues) return baseValues;

    const effectiveTotal = Number.isFinite(adjustedTotalWeight) ? adjustedTotalWeight : totalWeight;

    let multiplier = 1;
    if (mode === 'total') {
      multiplier = Number.isFinite(effectiveTotal) ? effectiveTotal / 100 : 1;
    } else if (mode === 'perServing') {
      if (Number.isFinite(effectiveTotal)) {
        multiplier = (effectiveTotal / 100) / safeServings;
      } else {
        multiplier = 1 / safeServings;
      }
    }

    return {
      calories: Math.round(baseValues.calories * multiplier),
      protein: Math.round(baseValues.protein * multiplier * 10) / 10,
      carbs: Math.round(baseValues.carbs * multiplier * 10) / 10,
      sugar: Math.round(baseValues.sugar * multiplier * 10) / 10,
      fat: Math.round(baseValues.fat * multiplier * 10) / 10,
      saturatedFat: Math.round(baseValues.saturatedFat * multiplier * 10) / 10,
      fiber: Math.round(baseValues.fiber * multiplier * 10) / 10,
      salt: Math.round(baseValues.salt * multiplier * 100) / 100
    };
  };

  const displayValues = calculateValues(nutriValues, viewMode);

  const getModeLabel = () => {
    switch(viewMode) {
      case 'per100g': return 'pour 100g';
      case 'perServing': return 'par portion';
      case 'total': return 'tout le plat';
      default: return 'pour 100g';
    }
  };

  const calculateGlycemicLoad = (glycemicIndex, carbs) => {
    return Math.round((glycemicIndex * carbs) / 100);
  };

  const getGlycemicIndexCategory = (gi) => {
    if (gi <= 35) return { label: 'Faible', color: '#2C3E50', bg: '#d1fae5' };
    if (gi <= 50) return { label: 'Modéré', color: '#92400e', bg: '#fef3c7' };
    return { label: 'Élevé', color: '#991b1b', bg: '#fecaca' };
  };

  const getGlycemicLoadCategory = (gl) => {
    if (gl <= 10) return { label: 'Faible', color: '#2C3E50', bg: '#d1fae5' };
    if (gl <= 19) return { label: 'Modérée', color: '#92400e', bg: '#fef3c7' };
    return { label: 'Élevée', color: '#991b1b', bg: '#fecaca' };
  };

  const glycemicLoad = displayValues && nutriValues?.glycemicIndex ? 
    calculateGlycemicLoad(nutriValues.glycemicIndex, displayValues.carbs) : 0;

  const giCategory = nutriValues?.glycemicIndex ? getGlycemicIndexCategory(nutriValues.glycemicIndex) : null;
  const glCategory = getGlycemicLoadCategory(glycemicLoad);

  return (
    <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl border border-gray-200 p-4 sm:p-6 shadow-lg">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 space-y-3 sm:space-y-0">
        <h4 className="font-bold text-base sm:text-lg" style={{ color: '#2C3E50' }}>Nutri-Score</h4>
        <div 
          className="w-12 h-12 sm:w-14 sm:h-14 rounded-full flex items-center justify-center text-white font-bold text-xl sm:text-2xl shadow-md"
          style={{ backgroundColor: getScoreColor(nutriScore) }}
        >
          {getScoreLetter(nutriScore)}
        </div>
      </div>

      <div className="mb-4">
        <div className="flex justify-center items-center space-x-2 mb-2">
          {[1, 2, 3, 4, 5].map((score) => (
            <div
              key={score}
              className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-white text-sm font-bold ${
                score === nutriScore ? 'ring-2 ring-gray-800 scale-110' : 'opacity-70'
              } transition-all shadow-md`}
              style={{ backgroundColor: getScoreColor(score) }}
            >
              {getScoreLetter(score)}
            </div>
          ))}
        </div>
        <div className="flex justify-between text-xs" style={{ color: '#6c757d' }}>
          <span>Meilleur</span>
          <span>Moins bon</span>
        </div>
      </div>

      <div className="mb-4">
        <button
          onClick={() => setShowNutriValues(!showNutriValues)}
          className="flex items-center justify-between w-full p-3 bg-white rounded-xl border border-gray-200 hover:bg-gray-50 transition-colors shadow-sm"
        >
          <span className="font-semibold text-sm sm:text-base" style={{ color: '#2C3E50' }}>Valeurs nutritives</span>
          <svg 
            className={`w-5 h-5 transition-transform ${showNutriValues ? 'rotate-180' : ''}`}
            style={{ color: '#6c757d' }}
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {showNutriValues && (
          <div className="mt-3 space-y-4">
            <div className="flex flex-wrap gap-2">
              {[
                { key: 'per100g', label: '100g' },
                { key: 'perServing', label: 'Part' },
                { key: 'total', label: 'Plat' }
              ].map((mode) => (
                <button
                  key={mode.key}
                  onClick={() => setViewMode(mode.key)}
                  className={`px-3 py-1.5 text-sm rounded-full transition-colors shadow-sm ${
                    viewMode === mode.key
                      ? 'text-white'
                      : 'bg-gray-200 hover:bg-gray-300'
                  }`}
                  style={viewMode === mode.key 
                    ? { backgroundColor: '#87A96B' } 
                    : { color: '#2C3E50' }
                  }
                >
                  {mode.label}
                </button>
              ))}
            </div>
            <p className="text-xs" style={{ color: '#6c757d' }}>Valeurs {getModeLabel()}</p>

            {displayValues && (
              <div className="grid grid-cols-1 gap-3 text-sm">
                <div className="space-y-3">
                  <div className="flex justify-between bg-white p-2 rounded-lg shadow-sm">
                    <span style={{ color: '#6c757d' }}>Calories:</span>
                    <span className="font-semibold" style={{ color: '#2C3E50' }}>{displayValues.calories} kcal</span>
                  </div>
                  <div className="flex justify-between bg-white p-2 rounded-lg shadow-sm">
                    <span style={{ color: '#6c757d' }}>Protéines:</span>
                    <span className="font-semibold" style={{ color: '#2C3E50' }}>{displayValues.protein}g</span>
                  </div>
                  <div className="space-y-1">
                    <div className="flex justify-between bg-white p-2 rounded-lg shadow-sm">
                      <span style={{ color: '#6c757d' }}>Glucides:</span>
                      <span className="font-semibold" style={{ color: '#2C3E50' }}>{displayValues.carbs}g</span>
                    </div>
                    <div className="flex justify-between pl-4 text-xs bg-white p-2 rounded-lg shadow-sm">
                      <span style={{ color: '#6c757d' }}>(dont sucres):</span>
                      <span className="font-semibold" style={{ color: '#2C3E50' }}>{displayValues.sugar}g</span>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <div className="flex justify-between bg-white p-2 rounded-lg shadow-sm">
                      <span style={{ color: '#6c757d' }}>Lipides:</span>
                      <span className="font-semibold" style={{ color: '#2C3E50' }}>{displayValues.fat}g</span>
                    </div>
                    <div className="flex justify-between pl-4 text-xs bg-white p-2 rounded-lg shadow-sm">
                      <span style={{ color: '#6c757d' }}>(dont acides gras saturés):</span>
                      <span className="font-semibold" style={{ color: '#2C3E50' }}>{displayValues.saturatedFat}g</span>
                    </div>
                  </div>
                  <div className="flex justify-between bg-white p-2 rounded-lg shadow-sm">
                    <span style={{ color: '#6c757d' }}>Fibres:</span>
                    <span className="font-semibold" style={{ color: '#2C3E50' }}>{displayValues.fiber}g</span>
                  </div>
                  <div className="flex justify-between bg-white p-2 rounded-lg shadow-sm">
                    <span style={{ color: '#6c757d' }}>Sel:</span>
                    <span className="font-semibold" style={{ color: '#2C3E50' }}>{displayValues.salt}g</span>
                  </div>
                </div>
              </div>
            )}
          </div>

        )}
      </div>

      {nutriValues?.glycemicIndex && (
        <div className="mb-4">
          <button
            onClick={() => setShowGlycemicImpact(!showGlycemicImpact)}
            className="flex items-center justify-between w-full p-3 bg-white rounded-xl border border-gray-200 hover:bg-gray-50 transition-colors shadow-sm"
          >
            <span className="font-semibold text-sm sm:text-base" style={{ color: '#2C3E50' }}>Impact glycémique</span>
            <svg 
              className={`w-5 h-5 transition-transform ${showGlycemicImpact ? 'rotate-180' : ''}`}
              style={{ color: '#6c757d' }}
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {showGlycemicImpact && (
            <div className="mt-3 space-y-3">
              <div className="grid grid-cols-1 gap-3 text-sm">
                <div className="flex justify-between items-center bg-white p-2 rounded-lg shadow-sm">
                  <span style={{ color: '#6c757d' }}>Index glycémique:</span>
                  <div className="flex items-center space-x-2">
                    <span className="font-semibold" style={{ color: '#2C3E50' }}>{nutriValues.glycemicIndex}</span>
                    {giCategory && (
                      <span 
                        className="px-2 py-1 rounded-full text-xs shadow-sm"
                        style={{ backgroundColor: giCategory.bg, color: giCategory.color }}
                      >
                        {giCategory.label}
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex justify-between items-center bg-white p-2 rounded-lg shadow-sm">
                  <span style={{ color: '#6c757d' }}>Charge glycémique:</span>
                  <div className="flex items-center space-x-2">
                    <span className="font-semibold" style={{ color: '#2C3E50' }}>{glycemicLoad}</span>
                    <span 
                      className="px-2 py-1 rounded-full text-xs shadow-sm"
                      style={{ backgroundColor: glCategory.bg, color: glCategory.color }}
                    >
                      {glCategory.label}
                    </span>
                  </div>
                </div>
              </div>

              <div className="text-xs italic" style={{ color: '#6c757d' }}>
                {viewMode === 'per100g' ? 'Valeurs pour 100g' : 
                 viewMode === 'perServing' ? 'Valeurs par portion' : 
                 'Valeurs pour tout le plat'}
              </div>
            </div>
          )}
        </div>
      )}

      {nutriValues?.nutriDescription && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <h5 className="font-semibold text-sm sm:text-base mb-3" style={{ color: '#2C3E50' }}>Description nutritive</h5>
          <p className="text-sm leading-relaxed italic" style={{ color: '#6c757d' }}>{nutriValues.nutriDescription}</p>
        </div>
      )}
    </div>
  );
};

const RecipeDisplay = ({
  recipe,
  onSave,
  onNew,
  onVariantRequest,
  onAIQuestion,
  familyTree,
  changeAdults,
  changeTeens,
  changeChildren,
}) => {
  // Spinner SVG encodé en data-URL (autonome, aucune ressource externe)
  const SPINNER_DATA_URL =
    'data:image/svg+xml;base64,' +
    btoa(`
  <svg xmlns="http://www.w3.org/2000/svg" width="400" height="200" viewBox="0 0 100 100" preserveAspectRatio="xMidYMid">
    <rect width="100" height="100" fill="transparent"/>
    <g>
      <animateTransform attributeName="transform" type="rotate" repeatCount="indefinite" dur="0.9s" values="0 50 50;360 50 50" keyTimes="0;1"/>
      <path d="M50 14a36 36 0 1 0 36 36" fill="none" stroke="rgba(255,255,255,0.9)" stroke-width="8" stroke-linecap="round"/>
    </g>
  </svg>
  `);
  const [selectedVariants, setSelectedVariants] = useState([]);
  // Image à afficher : démarre sur spinner → passe à l'URL réelle quand chargée
  const [imageToDisplay, setImageToDisplay] = useState(SPINNER_DATA_URL);

  const [ingredientReplacements, setIngredientReplacements] = useState({});
  const [excludedIngredients, setExcludedIngredients] = useState([]);
  const [editingIngredient, setEditingIngredient] = useState(null);
  const [replacementValue, setReplacementValue] = useState('');
  const [comment, setComment] = useState('');
  const [aiQuestionIngredient, setAiQuestionIngredient] = useState(null);
  const [aiQuestionText, setAiQuestionText] = useState('');
  const [aiResponse, setAiResponse] = useState(null);
  const [lastPageType, setLastPageType] = useState(null);
  const [servings, setServings] = useState(2);
  const originalServings = 2;
  const [showIngredients, setShowIngredients] = useState(false); // affichage mobile
  // État pour tracker la vérification d'image
  const [imageVerified, setImageVerified] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const navigate = useNavigate();
  const hasLoadedOnce = useRef(false);

  const ingredientImageIndex = useMemo(() => {
    const map = new Map();
    if (!INGREDIENT_IMAGE_BASE || !Array.isArray(recipe?.ingredients)) {
      return map;
    }

    recipe.ingredients.forEach((ing) => {
      const standardId = extractStandardIngredientId(ing);
      if (!standardId) return;
      const url = buildIngredientImageUrl(standardId, INGREDIENT_IMAGE_BASE);
      if (!url) return;
      const keys = [normalizeIngredientKey(ing.name), normalizeIngredientKey(ing.normalizedName)];
      keys.forEach((key) => {
        if (key && !map.has(key)) {
          map.set(key, url);
        }
      });
    });

    return map;
  }, [recipe?.ingredients]);

  const derivedPrepTime = (() => {
    const value = Number(
      recipe?.prepTime ??
      recipe?.preparationTime ??
      recipe?.preparation_time
    );
    return Number.isFinite(value) && value > 0 ? value : null;
  })();

  const normalizedComplexity = typeof recipe?.complexity === 'string'
    ? recipe.complexity
    : null;

  const getIngredientImageUrl = (ingredient) => {
    if (!ingredient || !INGREDIENT_IMAGE_BASE) return null;

    const directId = extractStandardIngredientId(ingredient);
    if (directId) {
      return buildIngredientImageUrl(directId, INGREDIENT_IMAGE_BASE);
    }

    const candidates = [normalizeIngredientKey(ingredient.name), normalizeIngredientKey(ingredient.normalizedName)];
    for (const key of candidates) {
      if (key && ingredientImageIndex.has(key)) {
        return ingredientImageIndex.get(key);
      }
    }

    if (Array.isArray(recipe?.ingredients)) {
      const stepKeys = [normalizeIngredientKey(ingredient.name), normalizeIngredientKey(ingredient.normalizedName)];
      const matched = recipe.ingredients.find((baseIng) => {
        const baseKeys = [normalizeIngredientKey(baseIng.name), normalizeIngredientKey(baseIng.normalizedName)];
        return stepKeys.some((key) => key && baseKeys.includes(key));
      });
      if (matched) {
        const matchedId = extractStandardIngredientId(matched);
        if (matchedId) {
          return buildIngredientImageUrl(matchedId, INGREDIENT_IMAGE_BASE);
        }
      }
    }

    return null;
  };

  // Gestion de l'image : spinner -> préchargement -> backgroundImage
  useEffect(() => {
    if (hasLoadedOnce.current) return;
    hasLoadedOnce.current = true;

    console.log('ping')
    console.log(recipe.imgUrlArray)
    // Déterminer la meilleure source disponible dans ton objet recipe

    if (recipe && recipe.imgUrlArray.length===0) {
      // Pas d'image recette → on garde un fallback joli
      setImageToDisplay(SPINNER_DATA_URL);
      return;
    }
    else if (recipe && (recipe.imgUrlArray.length>0)) {
      var imgFoundRecipe=recipe.imgUrlArray.find(x=>x.indexOf('/recipe')>-1);
      var imgFoundSuggestion=recipe.imgUrlArray.find(x=>x.indexOf('/suggestion')>-1);
      if(imgFoundRecipe){
        setImageToDisplay(imgFoundRecipe);
        setImageVerified(true);
        return;
      }
      else if(imgFoundSuggestion){
        setImageToDisplay(imgFoundSuggestion)
        api.post('/api/meals/generate-images', {
          recipeId: recipe._id,
          prompts: [recipe.englishTitle+recipe.visualDescription],
        });
        // Démarrer la vérification d'image après génération
        setImageVerified(false);
      }
      else{
        api.post('/api/meals/generate-images', {
          recipeId: recipe._id,
          prompts: [recipe.englishTitle+recipe.visualDescription],
        });
        // Démarrer la vérification d'image après génération
        setImageVerified(false);
      }
    }
  }, [recipe?.imgUrlArray]);
  
  // Alternative avec timeout pour éviter une vérification infinie
  React.useEffect(() => {
    if (!recipe || !recipe._id || imageVerified) {
      return;
    }

    // Timeout après 2 minutes (12 vérifications)
    let checkCount = 0;
    const maxChecks = 12;

    const intervalId = setInterval(async () => {
      checkCount++;
      
      if (checkCount > maxChecks) {
        console.log('Max checks reached, stopping image verification');
        setImageVerified(true);
        return;
      }

      try {
        const { data } = await api.get(`/api/meals/check-images/${recipe._id}?type=recipe`);
        
        if (data.success && data.data.images && data.data.images.length > 0) {
          console.log('Images found!', data.data.images);
          
          // Trouver la meilleure image à afficher
          const imgFoundRecipe = data.data.images.find(x => x.indexOf('/recipe') > -1);
          const imgFoundSuggestion = data.data.images.find(x => x.indexOf('/suggestion') > -1);
          
          if (imgFoundRecipe) {
            setImageToDisplay(imgFoundRecipe);
            setImageVerified(true);
          } else if (imgFoundSuggestion) {
            setImageToDisplay(imgFoundSuggestion);
          } 
        } else {
          console.log('No images found yet, continuing to check...');
        }
      } catch (err) {
        console.error('Image check error:', err);
      }
    }, 10000);

    return () => clearInterval(intervalId);
  }, [recipe, imageVerified]);
  
  // Récup du lastPageType (URL)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const _lastPageType = urlParams.get('lastPageType');
    if (_lastPageType) setLastPageType(_lastPageType);
  }, []);

  const onBack = () => {
    if (['formInspiration', 'suggestions'].includes(lastPageType)) {
      navigate(`/inspirations-repas?lastPageType=${lastPageType}`);
    } else if (lastPageType === 'recipesList') {
      navigate('/mes-recettes');
    } 
    else if (lastPageType === 'dashboard') {
      navigate('/dashboard');
    } 
    else if (lastPageType === 'recipe') {
      const urlParams = new URLSearchParams(window.location.search);
      const lastRecipeId = urlParams.get('lastRecipeId');
      navigate('/recette/' + lastRecipeId);
    }
  };

  const adjustQuantityForServings = (originalQuantity, currentServings, originalServings) => {
    const numericQuantity = parseFloat(originalQuantity);
    if (isNaN(numericQuantity)) return originalQuantity;

    const adjustedQuantity = (numericQuantity * currentServings) / originalServings;

    if (adjustedQuantity < 1) {
      return adjustedQuantity.toFixed(1);
    } else if (adjustedQuantity < 10) {
      return Math.round(adjustedQuantity * 10) / 10;
    } else {
      return Math.round(adjustedQuantity);
    }
  };

  const handleVariantSelection = (variant) => {
    setSelectedVariants((prev) => {
      if (prev.includes(variant)) return prev.filter((v) => v !== variant);
      return [...prev, variant];
    });
  };

  const handleReplaceIngredient = (ingredientName) => {
    if (editingIngredient === ingredientName) {
      if (replacementValue.trim()) {
        setIngredientReplacements({
          ...ingredientReplacements,
          [ingredientName]: replacementValue.trim(),
        });

        if (excludedIngredients.includes(ingredientName)) {
          setExcludedIngredients(excludedIngredients.filter((item) => item !== ingredientName));
        }
      }
      setEditingIngredient(null);
      setReplacementValue('');
    } else {
      setEditingIngredient(ingredientName);
      setReplacementValue(ingredientReplacements[ingredientName] || '');
    }
  };

  const handleExcludeIngredient = (ingredientName) => {
    if (excludedIngredients.includes(ingredientName)) {
      setExcludedIngredients(excludedIngredients.filter((item) => item !== ingredientName));
      return;
    }

    setExcludedIngredients([...excludedIngredients, ingredientName]);

    if (ingredientReplacements[ingredientName]) {
      const newReplacements = { ...ingredientReplacements };
      delete newReplacements[ingredientName];
      setIngredientReplacements(newReplacements);
    }
  };

  const handleAIQuestionForIngredient = (ingredientName, ingredient) => {
    const adjustedQuantity = adjustQuantityForServings(ingredient.quantity, servings, originalServings);
    const defaultQuestion = `Par quoi puis-je remplacer "${ingredientName}" (${adjustedQuantity} ${ingredient.unity}) dans cette recette ?`;

    if (aiQuestionIngredient === ingredientName) {
      setAiQuestionIngredient(null);
      setAiQuestionText('');
    } else {
      setAiQuestionIngredient(ingredientName);
      setAiQuestionText(defaultQuestion);
    }
  };

  const handleSendAIQuestion = async () => {
    try {
      if (aiQuestionIngredient && aiQuestionText.trim()) {
        const ingredient = recipe.ingredients.find((ing) => ing.name === aiQuestionIngredient);

        const response = await api.post('/api/meals/question-about-recipe', {
          recipe: recipe,
          ingredient: aiQuestionIngredient,
          context: ingredient,
          question: aiQuestionText.trim(),
        });

        setAiResponse(response.data.data.answer);
      }
    } catch (error) {
      console.error('Error sending AI question:', error);
    }
  };

  const handleReplacementSelection = (replacementIngredient) => {
    setIngredientReplacements({
      ...ingredientReplacements,
      [aiQuestionIngredient]: replacementIngredient.name,
    });
    setAiResponse(null);
    setAiQuestionIngredient(null);
    setAiQuestionText('');
  };

  const handleCancelAIQuestion = () => {
    setAiQuestionIngredient(null);
    setAiQuestionText('');
  };

  const handleCancelReplacement = () => {
    setEditingIngredient(null);
    setReplacementValue('');

    if (editingIngredient && ingredientReplacements[editingIngredient]) {
      const newReplacements = { ...ingredientReplacements };
      delete newReplacements[editingIngredient];
      setIngredientReplacements(newReplacements);
    }
  };

  const applyVariantsAndReplacements = () => {
    const replacements =
      Object.keys(ingredientReplacements).length > 0 ? { ingredientReplacements } : {};

    const exclusions = excludedIngredients.length > 0 ? { excludedIngredients } : {};

    onVariantRequest({
      variants: selectedVariants,
      comment: comment.trim(),
      servings: servings,
      ...replacements,
      ...exclusions,
    });

    setComment('');
  };

  const hasModifications = () => {
    return (
      selectedVariants.length > 0 ||
      Object.keys(ingredientReplacements).length > 0 ||
      excludedIngredients.length > 0 ||
      comment.trim().length > 0 ||
      servings !== originalServings
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 overflow-x-hidden">
      <div className="max-w-4xl mx-auto sm:p-6">
        {lastPageType && (
          <button
            onClick={onBack}
            className="mb-4 px-4 py-2 bg-white rounded-full hover:bg-gray-100 transition-colors shadow-md flex items-center"
            style={{ color: '#2C3E50' }}
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
            </svg>
            Retour
          </button>
        )}

        <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
          <div
            className="relative h-80 sm:h-96"
            style={{
              backgroundImage: `linear-gradient(135deg, rgba(135, 169, 107, 0.8), rgba(230, 126, 34, 0.8)), url('${imageToDisplay}')`,
              backgroundBlendMode: 'multiply',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          >
            <button
              onClick={() => setShowImageModal(true)}
              disabled={imageToDisplay === SPINNER_DATA_URL}
              title={imageToDisplay === SPINNER_DATA_URL ? "Image en cours de génération" : "Voir l'image en plein écran"}
              className={`absolute top-4 right-4 z-20 p-2 rounded-full shadow-md transition-colors ${
                imageToDisplay === SPINNER_DATA_URL ? 'bg-white/60 cursor-not-allowed opacity-70' : 'bg-white/90 hover:bg-white'
              }`}
              style={{ color: '#2C3E50' }}
            >
              <Maximize2 className="w-5 h-5" />
            </button>
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
            <div className="relative z-10 p-6 sm:p-8 flex flex-col justify-end h-full">
              <div className="flex items-center mb-3 space-x-3">
                <DishTypeBadge type={recipe.dishType} />
              </div>
              <h1 className="text-3xl sm:text-4xl font-bold text-white drop-shadow-lg">{recipe.title}</h1>
              <div className="flex items-center space-x-4 text-white mt-3">
                <span className="flex items-center text-sm">
                  <Clock className="w-5 h-5 mr-2" />
                  {recipe.prepTime} min
                </span>
                <span className="flex items-center text-sm">
                  <ChefHat className="w-5 h-5 mr-2" />
                  {recipe.complexity}
                </span>
              </div>
            </div>
          </div>

          {showImageModal && (
            <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50">
              <button
                onClick={() => setShowImageModal(false)}
                className="absolute top-4 right-4 p-2 rounded-full bg-white/90 hover:bg-white shadow-md"
                title="Fermer"
                style={{ color: '#2C3E50' }}
              >
                <X className="w-5 h-5" />
              </button>
              <img
                src={imageToDisplay}
                alt={recipe.title}
                className="max-w-[95vw] max-h-[90vh] object-contain rounded-xl shadow-2xl"
              />
            </div>
          )}

          <div className=" sm:p-8 space-y-8">
            {familyTree && (
              <div style={{ backgroundColor: '#f8f9fa' }} className="rounded-2xl p-4 border border-gray-200 shadow-sm">
                <RecipeTree
                  familyTree={familyTree}
                  currentRecipeId={recipe.id}
                  currentPage={{ type: 'recipe', recipeId: recipe.id }}
                  currentImageUrl={imageVerified ? imageToDisplay : undefined}
                />
              </div>
            )}

            <div className="space-y-6">
              <p className="italic text-sm sm:text-base leading-relaxed" style={{ color: '#6c757d' }}>
                {recipe.description}
              </p>

              <div className="space-y-6">
                <NutriScoreDisplay
                  nutriScore={recipe.nutriScore}
                  nutriValues={recipe.nutriValues}
                  totalWeight={recipe.nutriValues?.totalWeight || 400}
                  servings={servings}
                  originalServings={originalServings}
                />
                <ServingsAdjuster
                  servings={servings}
                  onServingsChange={setServings}
                  originalServings={originalServings}
                  changeAdults={changeAdults}
                  changeTeens={changeTeens}
                  changeChildren={changeChildren}
                />
              </div>

              <div className="space-y-8">
                {/* INGREDIENTS */}
                <div className="bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50 rounded-2xl border border-orange-200 shadow-lg overflow-hidden">
                  <div
                    className="flex justify-between items-center p-4 sm:p-6 cursor-pointer sm:cursor-default"
                    onClick={() => setShowIngredients(!showIngredients)}
                  >
                    <h3 className="font-bold text-lg sm:text-xl flex items-center" style={{ color: '#2C3E50' }}>
                      <div className="p-2 rounded-xl mr-3" style={{ backgroundColor: '#E67E22' }}>
                        <Circle className="w-6 h-6 text-white" />
                      </div>
                      Ingrédients
                    </h3>
                    <span 
                      className="text-xs text-white px-3 py-1.5 rounded-full sm:hidden"
                      style={{ backgroundColor: '#87A96B' }}
                    >
                      {showIngredients ? '−' : '+'}
                    </span>
                  </div>

                  <div
                    className={`${showIngredients ? 'block' : 'hidden'} sm:block px-4 sm:px-6 pb-6 space-y-4`}
                  >
                    {recipe.ingredients.map((ingredient, index) => {
                      const ingredientName = ingredient.name;
                      const isReplaced = ingredientReplacements[ingredientName];
                      const isExcluded = excludedIngredients.includes(ingredientName);
                      const isEditing = editingIngredient === ingredientName;
                      const isAskingAI = aiQuestionIngredient === ingredientName;
                      const adjustedQuantity = adjustQuantityForServings(
                        ingredient.quantity,
                        servings,
                        originalServings
                      );
                      const detailUrl = buildIngredientDetailUrl(ingredient);
                      const nameClassNames = `font-semibold ${
                        isExcluded
                          ? 'line-through text-red-400'
                          : isReplaced
                          ? 'line-through text-gray-400'
                          : ''
                      } ${detailUrl && !isExcluded && !isReplaced ? 'hover:underline hover:text-[#E67E22]' : ''}`.trim();
                      const nameStyle = isExcluded || isReplaced ? undefined : { color: '#2C3E50' };

                      return (
                        <div
                          key={index}
                          className={`bg-white rounded-xl p-4 border ${
                            isExcluded
                              ? 'border-red-200 bg-red-50'
                              : isReplaced
                              ? 'border-green-200 bg-green-50'
                              : 'border-gray-200'
                          } shadow-md hover:shadow-lg transition-all`}
                        >
                          <div className="flex items-center gap-4">
                            {(() => {
                              const url = getIngredientImageUrl(ingredient);
                              if (!url) return null;
                              return (
                                <img
                                  src={url}
                                  alt={ingredientName}
                                  className="h-14 w-14 rounded-full object-cover border border-gray-200 bg-gray-50"
                                />
                              );
                            })()}
                            <div className="flex-grow">
                              <div className="flex items-center mb-2">
                                {detailUrl ? (
                                  <Link
                                    to={detailUrl}
                                    className={nameClassNames}
                                    style={nameStyle}
                                  >
                                    {ingredientName}
                                  </Link>
                                ) : (
                                  <span
                                    className={nameClassNames}
                                    style={nameStyle}
                                  >
                                    {ingredientName}
                                  </span>
                                )}
                                {isReplaced && (
                                  <span className="ml-2 text-sm font-medium" style={{ color: '#87A96B' }}>
                                    → {ingredientReplacements[ingredientName]}
                                  </span>
                                )}
                                {isExcluded && (
                                  <span className="ml-2 text-red-500 text-sm font-medium">
                                    (retiré)
                                  </span>
                                )}
                              </div>
                              <span className="text-sm" style={{ color: '#6c757d' }}>
                                {adjustedQuantity} {ingredient.unity}
                              </span>
                            </div>

                            <div className="flex items-center gap-3">
                              <div className="flex space-x-2">
                                <button
                                  onClick={() => handleReplaceIngredient(ingredientName)}
                                  className={`p-2 rounded-full transition-colors ${
                                    isExcluded ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-100'
                                  }`}
                                  style={{ color: isExcluded ? '#6c757d' : '#87A96B' }}
                                  title={isReplaced ? 'Modifier le remplacement' : 'Remplacer cet ingrédient'}
                                  disabled={isExcluded}
                                >
                                  <svg
                                    className="w-5 h-5"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth="2"
                                      d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                                    />
                                  </svg>
                                </button>

                                <button
                                  onClick={() => handleAIQuestionForIngredient(ingredientName, ingredient)}
                                  className={`${
                                    isAskingAI ? 'bg-purple-100' : ''
                                  } p-2 rounded-full hover:bg-purple-100 transition-colors ${
                                    isExcluded ? 'opacity-50 cursor-not-allowed' : ''
                                  }`}
                                  style={{ color: isAskingAI ? '#7c3aed' : '#E67E22' }}
                                  title="Demander des suggestions de remplacement"
                                  disabled={isExcluded}
                                >
                                  <svg
                                    className="w-5 h-5"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth="2"
                                      d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                                    />
                                  </svg>
                                </button>

                                <button
                                  onClick={() => handleExcludeIngredient(ingredientName)}
                                  className={`${
                                    isExcluded ? 'hover:bg-green-100' : 'hover:bg-red-100'
                                  } p-2 rounded-full transition-colors`}
                                  style={{ color: isExcluded ? '#87A96B' : '#dc2626' }}
                                  title={isExcluded ? 'Réintégrer cet ingrédient' : "Je ne veux pas cet élément"}
                                >
                                  {isExcluded ? (
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="2"
                                        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                                      />
                                    </svg>
                                  ) : (
                                    <X className="w-5 h-5" />
                                  )}
                                </button>
                              </div>
                            </div>
                          </div>

                          {isEditing && (
                            <div className="flex mt-3 space-x-2">
                              <input
                                type="text"
                                value={replacementValue}
                                onChange={(e) => setReplacementValue(e.target.value)}
                                placeholder="Remplacer par..."
                                className="flex-grow px-3 py-2 border rounded-l-xl text-sm shadow-sm focus:ring-2"
                                style={{ accentColor: '#87A96B' }}
                              />
                              <button
                                onClick={() => handleReplaceIngredient(ingredientName)}
                                className="text-white px-4 py-2 rounded-r-xl shadow-sm hover:opacity-90 transition-colors"
                                style={{ backgroundColor: '#87A96B' }}
                              >
                                OK
                              </button>
                              <button
                                onClick={handleCancelReplacement}
                                className="bg-gray-200 px-4 py-2 rounded-xl shadow-sm hover:bg-gray-300 transition-colors"
                                style={{ color: '#2C3E50' }}
                              >
                                ✕
                              </button>
                            </div>
                          )}

                          {isAskingAI && (
                            <div className="mt-3 p-4 bg-purple-50 rounded-xl border border-purple-200 shadow-sm">
                              <div className="flex items-center mb-3">
                                <svg
                                  className="w-5 h-5 mr-2"
                                  style={{ color: '#E67E22' }}
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                                  />
                                </svg>
                                <span className="text-sm font-semibold" style={{ color: '#2C3E50' }}>
                                  Poser une question à YAQAM à propos de l&apos;ingrédient
                                </span>
                              </div>

                              {!aiResponse ? (
                                <>
                                  <textarea
                                    value={aiQuestionText}
                                    onChange={(e) => setAiQuestionText(e.target.value)}
                                    placeholder="Posez votre question sur cet ingrédient..."
                                    className="w-full px-3 py-2 border border-purple-300 rounded-xl text-sm min-h-[80px] shadow-sm focus:ring-2 focus:border-transparent"
                                    style={{ accentColor: '#87A96B' }}
                                  />
                                  <div className="flex justify-end space-x-2 mt-3">
                                    <button
                                      onClick={handleCancelAIQuestion}
                                      className="px-4 py-2 text-sm bg-gray-100 rounded-xl hover:bg-gray-200 shadow-sm"
                                      style={{ color: '#6c757d' }}
                                    >
                                      Annuler
                                    </button>
                                    <button
                                      onClick={handleSendAIQuestion}
                                      disabled={!aiQuestionText.trim()}
                                      className={`px-4 py-2 text-sm rounded-xl shadow-sm ${
                                        aiQuestionText.trim()
                                          ? 'text-white hover:opacity-90'
                                          : 'bg-gray-200 cursor-not-allowed'
                                      }`}
                                      style={aiQuestionText.trim() 
                                        ? { backgroundColor: '#E67E22' } 
                                        : { color: '#6c757d' }
                                      }
                                    >
                                      Poser la question
                                    </button>
                                  </div>
                                </>
                              ) : (
                                <div className="space-y-4">
                                  <p className="text-sm" style={{ color: '#2C3E50' }}>
                                    {aiResponse?.answer || aiResponse}
                                  </p>

                                  {aiResponse?.ingredientsSuggestedForReplacement && (
                                    <div className="space-y-3">
                                      <h4 className="text-sm font-semibold" style={{ color: '#2C3E50' }}>
                                        Suggestions de remplacement :
                                      </h4>
                                      <div className="grid grid-cols-2 gap-3">
                                        {aiResponse.ingredientsSuggestedForReplacement.map(
                                          (ing, idx) => (
                                            <button
                                              key={idx}
                                              onClick={() => handleReplacementSelection(ing)}
                                              className="text-left p-3 bg-white border border-purple-200 rounded-xl hover:bg-purple-50 transition-colors shadow-sm"
                                            >
                                              <span className="font-semibold" style={{ color: '#2C3E50' }}>
                                                {ing.name}
                                              </span>
                                              <br />
                                              <span className="text-sm" style={{ color: '#6c757d' }}>
                                                {adjustQuantityForServings(
                                                  ing.quantity,
                                                  servings,
                                                  originalServings
                                                )}{' '}
                                                {ing.unity}
                                              </span>
                                            </button>
                                          )
                                        )}
                                      </div>
                                    </div>
                                  )}

                                  <div className="flex justify-end">
                                    <button
                                      onClick={handleCancelAIQuestion}
                                      className="px-4 py-2 text-sm bg-gray-100 rounded-xl hover:bg-gray-200 shadow-sm"
                                      style={{ color: '#6c757d' }}
                                    >
                                      Fermer
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* INSTRUCTIONS */}
                <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-lg">
                  <h3 className="font-bold text-lg sm:text-xl mb-5" style={{ color: '#2C3E50' }}>Instructions</h3>
                  <ol className="space-y-6">
                    {recipe.instructions.map((step, index) => (
                      <li
                        key={index}
                        className="border-l-4 bg-gradient-to-r from-gray-50 to-gray-100 p-5 rounded-xl shadow-sm"
                        style={{ borderLeftColor: '#87A96B' }}
                      >
                        <div className="flex items-center mb-4">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-r from-green-100 to-emerald-100 flex items-center justify-center mr-4 font-bold text-lg shadow-md"
                               style={{ color: '#87A96B' }}>
                            {index + 1}
                          </div>
                          <span className="font-semibold" style={{ color: '#2C3E50' }}>Étape {index + 1}</span>
                        </div>

                        {step.allIngredientUsed && step.allIngredientUsed.length > 0 && (
                          <div className="mb-4 p-4 rounded-xl border shadow-sm" style={{ backgroundColor: '#e8f5e8', borderColor: '#87A96B' }}>
                            <ul className="space-y-2">
                              {step.allIngredientUsed.map((ing, ingredientIndex) => {
                                // Helper pour arrondir joliment
                                const roundDisplay = (val) => {
                                  const num = parseFloat(val);
                                  if (isNaN(num)) return val;
                                  if (num < 1) return num.toFixed(2);
                                  if (num < 10) return Math.round(num * 10) / 10;
                                  return Math.round(num);
                                };

                                let qtyToShow;
                                let unitToShow;
                                if ((ing.unity === '%') && Array.isArray(recipe.ingredients)) {
                                  const baseIng = recipe.ingredients.find((rIng) => rIng.name === ing.name);
                                  const baseQty = baseIng ? parseFloat(baseIng.quantity) : NaN;
                                  const perc = parseFloat(ing.quantityUsed);
                                  if (!isNaN(baseQty) && !isNaN(perc)) {
                                    const adjBase = parseFloat(
                                      adjustQuantityForServings(baseQty, servings, originalServings)
                                    );
                                    const computed = (adjBase * perc) / 100;
                                    qtyToShow = roundDisplay(computed);
                                    unitToShow = baseIng?.unity || '';
                                  }
                                }

                                // Fallback au comportement standard si non calculé
                                if (qtyToShow === undefined) {
                                  qtyToShow = adjustQuantityForServings(
                                    ing.quantityUsed,
                                    servings,
                                    originalServings
                                  );
                                  unitToShow = ing.unity;
                                }

                                const imageUrl = getIngredientImageUrl(ing);

                                return (
                                  <li
                                    key={ingredientIndex}
                                    className="text-sm flex items-center gap-3"
                                    style={{ color: '#2C3E50' }}
                                  >
                                    {imageUrl ? (
                                      <img
                                        src={imageUrl}
                                        alt={ing.name}
                                        className="h-12 w-12 rounded-full object-cover border border-gray-200 bg-gray-50"
                                      />
                                    ) : (
                                      <span className="h-12 w-12 rounded-full border border-gray-200 bg-gray-100 flex items-center justify-center">
                                        <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: '#87A96B' }}></span>
                                      </span>
                                    )}
                                    <div className="flex items-center flex-wrap gap-2">
                                      <span className="font-semibold">{ing.name}</span>
                                      <span className="mx-1">:</span>
                                      <span style={{ color: '#6c757d' }}>
                                        {qtyToShow} {unitToShow}
                                      </span>
                                    </div>
                                  </li>
                                );
                              })}
                            </ul>
                          </div>
                        )}

                        <p className="text-sm sm:text-base leading-relaxed" style={{ color: '#2C3E50' }}>
                          <ExplanationWithTimes
                            text={step.step}
                            /* No timings here to avoid duplicate timers in detail view */
                            ingredients={recipe.ingredients}
                          />
                        </p>

                        {step.tips && (
                          <div className="mt-4 p-4 bg-gradient-to-br rounded-xl flex items-start shadow-sm border"
                               style={{ backgroundColor: '#fff3cd', borderColor: '#E67E22' }}>
                            <span className="text-xl mr-3">💡</span>
                            <div>
                              <p className="text-sm font-semibold" style={{ color: '#2C3E50' }}>Conseil de YAQAM</p>
                              <p className="text-sm mt-2" style={{ color: '#6c757d' }}>{step.tips}</p>
                            </div>
                          </div>
                        )}

                        {step.ListOfTimingsInStep && step.ListOfTimingsInStep.length > 0 && (
                          <div className="mt-3 text-xs" style={{ color: '#6c757d' }}>
                            ⏱️{' '}
                            {step.ListOfTimingsInStep
                              .map((timing) => {
                                const base = timing.duration || 0;
                                const additionalTimePerPersonInSecond = timing?.adjustmentFactor?.additionalTimePerPersonInSecond || 0;
                                const maxAdd = timing?.adjustmentFactor?.max;
                                const diff = Math.max(0, servings - originalServings);
                                const added = additionalTimePerPersonInSecond > 0 ? additionalTimePerPersonInSecond * diff : 0;
                                const capped = typeof maxAdd === 'number' && maxAdd >= 0 ? Math.min(added, maxAdd) : added;
                                const total = base + capped;
                                const mins = Math.floor(total / 60);
                                const secs = Math.round(total % 60);
                                const human = total < 60
                                  ? `${secs}s`
                                  : total < 3600
                                  ? secs ? `${mins} min ${secs}s` : `${mins} min`
                                  : `${Math.floor(total / 3600)} h ${Math.floor((total % 3600) / 60) || 0} min`;
                                return `${timing.label}: ${human}`;
                              })
                              .join(', ')}
                          </div>
                        )}
                      </li>
                    ))}
                  </ol>
                </div>

                <div className="rounded-2xl border border-green-200 bg-gradient-to-br from-green-50 via-emerald-50 to-white p-6 shadow-sm">
                  <h3 className="text-lg sm:text-xl font-semibold mb-4" style={{ color: '#2C3E50' }}>
                    ✨ Pourquoi cette idée de recette va vous plaire
                  </h3>
                  <div className="space-y-4">
                    {Array.isArray(recipe?.benefits) && recipe.benefits.length > 0 ? (
                      <ul className="space-y-3">
                        {recipe.benefits.map((benefit, index) => (
                          <li key={index} className="flex items-start gap-3">
                            <span className="text-green-500 text-lg mt-0.5">✓</span>
                            <span className="text-sm leading-relaxed" style={{ color: '#2C3E50' }}>{benefit}</span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <div className="space-y-3">
                        <div className="flex items-start gap-3">
                          <span className="text-green-500 text-lg mt-0.5">✓</span>
                          <span className="text-sm leading-relaxed" style={{ color: '#2C3E50' }}>
                            <strong>Facile à préparer :</strong> Une recette accessible qui ne demande pas de techniques compliquées
                          </span>
                        </div>
                        <div className="flex items-start gap-3">
                          <span className="text-green-500 text-lg mt-0.5">✓</span>
                          <span className="text-sm leading-relaxed" style={{ color: '#2C3E50' }}>
                            <strong>Ingrédients disponibles :</strong> Tous les ingrédients se trouvent facilement en magasin
                          </span>
                        </div>
                        <div className="flex items-start gap-3">
                          <span className="text-green-500 text-lg mt-0.5">✓</span>
                          <span className="text-sm leading-relaxed" style={{ color: '#2C3E50' }}>
                            <strong>Goût garanti :</strong> Une combinaison de saveurs équilibrée qui plaira à toute la famille
                          </span>
                        </div>
                        {recipe?.healthBenefits && (
                          <div className="flex items-start gap-3">
                            <span className="text-green-500 text-lg mt-0.5">✓</span>
                            <span className="text-sm leading-relaxed" style={{ color: '#2C3E50' }}>
                              <strong>Bon pour la santé :</strong> {recipe.healthBenefits}
                            </span>
                          </div>
                        )}
                        {normalizedComplexity && normalizedComplexity.toLowerCase().includes('facile') && (
                          <div className="flex items-start gap-3">
                            <span className="text-green-500 text-lg mt-0.5">✓</span>
                            <span className="text-sm leading-relaxed" style={{ color: '#2C3E50' }}>
                              <strong>Niveau débutant :</strong> Parfait pour se lancer en cuisine sans stress
                            </span>
                          </div>
                        )}
                        {derivedPrepTime && derivedPrepTime <= 30 && (
                          <div className="flex items-start gap-3">
                            <span className="text-green-500 text-lg mt-0.5">✓</span>
                            <span className="text-sm leading-relaxed" style={{ color: '#2C3E50' }}>
                              <strong>Rapide à préparer :</strong> Seulement {derivedPrepTime} minutes de préparation
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* COMMENTAIRES */}
                <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-lg">
                  <h3 className="font-bold text-lg sm:text-xl mb-4" style={{ color: '#2C3E50' }}>Commentaires et souhaits</h3>
                  <textarea
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    className="w-full p-4 border rounded-xl min-h-[120px] shadow-sm focus:ring-2 focus:border-transparent"
                    style={{ color: '#2C3E50', accentColor: '#87A96B' }}
                    placeholder="Ajoutez vos commentaires et souhaits sur cette recette..."
                  />
                </div>

                {/* VARIANTES */}
                <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-lg">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="font-bold text-lg sm:text-xl" style={{ color: '#2C3E50' }}>Variantes et suggestions</h3>
                    <span className="text-xs text-white px-3 py-1.5 rounded-full" style={{ backgroundColor: '#87A96B' }}>
                      Personnalisable
                    </span>
                  </div>

                  <div className="grid md:grid-cols-3 gap-6">
                    <div className="space-y-3">
                      <h4 className="font-semibold text-base" style={{ color: '#2C3E50' }}>Variations</h4>
                      {recipe.variantSuggestions.map((variant, index) => (
                        <div key={index} className="flex items-center space-x-3">
                          <input
                            type="checkbox"
                            id={`variant-${index}`}
                            checked={selectedVariants.includes(variant)}
                            onChange={() => handleVariantSelection(variant)}
                            className="h-5 w-5 rounded focus:ring-2"
                            style={{ accentColor: '#87A96B' }}
                          />
                          <label htmlFor={`variant-${index}`} className="text-sm" style={{ color: '#2C3E50' }}>
                            {variant}
                          </label>
                        </div>
                      ))}
                    </div>

                    <div className="space-y-3">
                      <h4 className="font-semibold text-base" style={{ color: '#2C3E50' }}>Améliorations</h4>
                      {recipe.improvementSuggestions.map((suggestion, index) => (
                        <div key={index} className="flex items-center space-x-3">
                          <input
                            type="checkbox"
                            id={`improve-${index}`}
                            checked={selectedVariants.includes(suggestion)}
                            onChange={() => handleVariantSelection(suggestion)}
                            className="h-5 w-5 rounded focus:ring-2"
                            style={{ accentColor: '#87A96B' }}
                          />
                          <label htmlFor={`improve-${index}`} className="text-sm" style={{ color: '#2C3E50' }}>
                            {suggestion}
                          </label>
                        </div>
                      ))}
                    </div>

                    <div className="space-y-3">
                      <h4 className="font-semibold text-base" style={{ color: '#2C3E50' }}>Accompagnements</h4>
                      {recipe.sideDishSuggestions.map((dish, index) => (
                        <div key={index} className="flex items-center space-x-3">
                          <input
                            type="checkbox"
                            id={`side-${index}`}
                            checked={selectedVariants.includes(dish)}
                            onChange={() => handleVariantSelection(dish)}
                            className="h-5 w-5 rounded focus:ring-2"
                            style={{ accentColor: '#87A96B' }}
                          />
                          <label htmlFor={`side-${index}`} className="text-sm" style={{ color: '#2C3E50' }}>
                            {dish}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="mt-6 pt-4 border-t border-gray-200">
                    <button
                      onClick={applyVariantsAndReplacements}
                      disabled={!hasModifications()}
                      className={`w-full py-3 rounded-full flex items-center justify-center text-base font-semibold shadow-md transition-all ${
                        hasModifications()
                          ? 'text-white hover:opacity-90'
                          : 'bg-gray-200 cursor-not-allowed'
                      }`}
                      style={hasModifications() 
                        ? { backgroundColor: '#87A96B' } 
                        : { color: '#6c757d' }
                      }
                    >
                      Générer une nouvelle version personnalisée
                    </button>

                    {!hasModifications() && (
                      <p className="text-center text-sm mt-3" style={{ color: '#6c757d' }}>
                        Ajoutez un commentaire, sélectionnez une option, changez le nombre de personnes
                        ou remplacez un ingrédient
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* FOOTER */}
            <div className="px-6 py-4 flex flex-col sm:flex-row gap-3 sm:gap-4 sm:justify-between sm:items-center border-t border-gray-200 rounded-b-3xl" style={{ backgroundColor: '#f8f9fa' }}>
              <button
                onClick={() => onSave(recipe)}
                className="w-full sm:w-auto text-white px-6 py-3 rounded-full hover:opacity-90 transition-colors shadow-md flex items-center justify-center"
                style={{ backgroundColor: '#87A96B' }}
              >
                <CheckCircle className="w-5 h-5 mr-2 flex-shrink-0" />
                <span className="text-center">Ajouter à la liste des recettes à faire</span>
              </button>
              <button
                onClick={onNew}
                className="w-full sm:w-auto bg-gray-200 px-6 py-3 rounded-full hover:bg-gray-300 transition-colors shadow-md flex items-center justify-center"
                style={{ color: '#2C3E50' }}
              >
                <Play className="w-5 h-5 mr-2 flex-shrink-0" />
                <span className="text-center">Nouvelle inspiration</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RecipeDisplay;
